import { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Drawer, Fab, IconButton, useMediaQuery } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectIsPackagingDraft, selectIsToolboxDrawerOpen } from '@/redux/selectors/packagingSelectors';
import { setIsToolboxDrawerOpen } from '@/redux/slices/packagingSlice';
import PolarisIcon from '../_common/PolarisIcon';
import ToolboxOptions from './ToolboxOptions';

export const closeHeight = 0;
export const defaultDrawerHeight = 450;
const minDrawerHeight = 150;
const maxDrawerHeight = 800;

const ToolboxDrawer = () => {
  const dispatch = useAppDispatch();
  const isToolboxDrawerOpen = useAppSelector(selectIsToolboxDrawerOpen);
  const [drawerHeight, setDrawerHeight] = useState<number>(closeHeight);
  const drawerRef = useRef<HTMLDivElement>(null);
  const [isTransitionEnabled, setIsTransitionEnabled] = useState<boolean>(true);
  const isLargeView = useMediaQuery((theme: any) => theme.breakpoints.up('md'));
  const isDraft = useAppSelector(selectIsPackagingDraft);

  const handleMouseDown = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    setIsTransitionEnabled(false);
    document.addEventListener('mouseup', handleMouseUp, true);
    document.addEventListener('mousemove', handleMouseMove, true);
  };

  const handleMouseUp = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    setIsTransitionEnabled(true);
    document.removeEventListener('mouseup', handleMouseUp, true);
    document.removeEventListener('mousemove', handleMouseMove, true);
  };

  const handleMouseMove = useCallback((e: any) => {
    if (!drawerRef.current) return;
    const deltaY = drawerRef.current.offsetTop - e.clientY + 50;
    if (deltaY > minDrawerHeight && deltaY < maxDrawerHeight) {
      setDrawerHeight(deltaY);
    }
  }, []);

  const handleTabClick = () => {
    dispatch(setIsToolboxDrawerOpen(!isToolboxDrawerOpen));
  };

  useEffect(() => {
    setDrawerHeight(isToolboxDrawerOpen ? defaultDrawerHeight : closeHeight);
  }, [isToolboxDrawerOpen]);

  if (!isDraft) return null;
  return isLargeView ? (
    <>
      <StyledFab variant="extended" color="primary" aria-label="add" onClick={handleTabClick}>
        <PolarisIcon icon="suitcase" height={15} />
        <span>Toolbox</span>
      </StyledFab>

      <DrawerBuffer height={drawerHeight} />
      <FixedDiv ref={drawerRef}>
        <RelativeDiv>
          <StyledDrawer
            variant="permanent"
            anchor="bottom"
            open={drawerHeight === closeHeight}
            height={drawerHeight}
            transition={+isTransitionEnabled}
          >
            <DrawerHeader onMouseDown={handleMouseDown}>
              <div style={{ marginLeft: 10 }}>
                <PolarisIcon icon="suitcase" height={15} />
                <span>Toolbox</span>
              </div>
              <Grip>
                <PolarisIcon icon="grip" height={20} />
              </Grip>
              <div>
                <IconButton onClick={handleTabClick}>
                  <PolarisIcon icon="down" height={15} />
                </IconButton>
              </div>
            </DrawerHeader>
            <ToolboxOptions />
          </StyledDrawer>
        </RelativeDiv>
      </FixedDiv>
    </>
  ) : (
    <ToolboxOptions isInline />
  );
};

export default ToolboxDrawer;

const RelativeDiv = styled.div`
  position: relative;
  z-index: 9999;
`;

const FixedDiv = styled.div`
  width: 100%;
  padding: ${({ theme }) => theme.spacing(2)};
  z-index: 999;
  position: fixed;
  bottom: 0;
`;

const DrawerHeader = styled.div`
  background: ${({ theme }) => theme.palette.primary.dark};
  height: 30px;
  width: 100%;
  cursor: row-resize;
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-radius: 5px 5px 0px 0px;
  padding: ${({ theme }) => theme.spacing(0, 2)};
  color: ${({ theme }) => theme.palette.getContrastText(theme.palette.primary.dark)};

  .MuiIconButton-root {
    color: ${({ theme }) => theme.palette.getContrastText(theme.palette.primary.dark)};
  }

  div {
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: ${({ theme }) => theme.spacing(0.75)};

    span {
      font-size: 14px;
      font-weight: 500;
    }
  }
`;

const StyledDrawer = styled(Drawer)<{ height: number; transition: number }>`
  .MuiDrawer-paper {
    border: none;
    overflow: hidden;
  }

  .MuiPaper-root {
    height: ${({ height }) => height}px;
    background: transparent;
  }

  ${({ transition }) =>
    transition
      ? `
    .MuiPaper-root {
      transition: height 0.2s ease-in-out;
    }  
  `
      : ''}
`;

const Grip = styled.div`
  height: 20px;
  cursor: row-resize;

  display: flex;
  justify-content: center;
  z-index: 1000;
`;

const DrawerBuffer = styled.div<{ height: number }>`
  height: ${({ height }) => (height ? height : 0)}px;
`;

const StyledFab = styled(Fab)`
  position: fixed;
  bottom: 100px;
  right: 50px;
  z-index: 998;

  span {
    font-size: 1.2rem;
    text-transform: none;
    margin-left: ${({ theme }) => theme.spacing(1)};
  }
`;
